/**
 * Safe Supabase Integration for Sentry
 *
 * Wraps @supabase/sentry-js-integration to handle Turbopack module loading
 * where SupabaseClient.prototype.from may be undefined at init time.
 *
 * Problem: With Next.js 16 + Turbopack, the module loading order differs from Webpack.
 * When Sentry initializes, SupabaseClient.prototype.from may be undefined, causing:
 * "TypeError: Cannot create proxy with a non-object as target or handler"
 *
 * Solution: Check prototype availability before creating the integration.
 * If unavailable, gracefully skip (error tracking still works, just no DB query tracing).
 *
 * @see https://cmg-frameworks.sentry.io/issues/JAVASCRIPT-NEXTJS-Q
 */
import type { Integration } from '@sentry/core';

type SupabaseIntegrationOptions = {
  tracing?: boolean;
  breadcrumbs?: boolean;
  errors?: boolean;
};

/**
 * Creates a defensive Supabase integration that won't crash if
 * SupabaseClient.prototype.from is undefined during module loading.
 *
 * @param SupabaseClient - The SupabaseClient constructor
 * @param Sentry - The Sentry SDK namespace
 * @param options - Integration options
 * @returns Sentry Integration or null if unable to initialize
 */
export function createSafeSupabaseIntegration(
  SupabaseClient: unknown,
  Sentry: typeof import('@sentry/nextjs'),
  options: SupabaseIntegrationOptions = {}
): Integration | null {
  // Check 1: SupabaseClient must exist
  if (!SupabaseClient) {
    // intentional: Supabase not available yet, skip integration silently
    return null;
  }

  // Check 2: Must have prototype
  const proto = (SupabaseClient as { prototype?: { from?: unknown } }).prototype;
  if (!proto) {
    // intentional: prototype not available, skip integration silently
    return null;
  }

  // Check 3: prototype.from must be a function (the Proxy target)
  // This is the critical check - Turbopack may not have attached the method yet
  if (typeof proto.from !== 'function') {
    // intentional: Supabase client not fully initialized, skip integration silently
    return null;
  }

  // Safe to proceed - prototype.from exists and is a function
  try {
    // Use require() to defer loading until after checks pass
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { supabaseIntegration } = require('@supabase/sentry-js-integration');
    return supabaseIntegration(SupabaseClient, Sentry, {
      tracing: options.tracing ?? true,
      breadcrumbs: options.breadcrumbs ?? true,
      errors: options.errors ?? true,
    });
  } catch (_) {
    // intentional: Supabase/Sentry integration is optional, fail silently
    return null;
  }
}

/**
 * Filter null/undefined integrations from array.
 * Use this to clean up the integrations array after adding safe wrappers.
 *
 * @example
 * integrations: filterNullIntegrations([
 *   Sentry.breadcrumbsIntegration({...}),
 *   createSafeSupabaseIntegration(...), // May return null
 *   Sentry.replayIntegration({...}),
 * ])
 */
export function filterNullIntegrations(
  integrations: (Integration | null | undefined)[]
): Integration[] {
  return integrations.filter((i): i is Integration => i != null);
}
